<template>
  <HrPageLayout
    title="Manage Leave Types"
    subtitle="Configure leave type allocations and settings"
    icon="📋"
    :breadcrumb-items="[
      { label: 'Leave & Attendance' },
      { label: 'Leave Types' }
    ]"
  >
    <template #header-actions>
      <div class="hr-flex hr-flex-center hr-gap-2">
        <select class="hr-select" style="width: 140px;" v-model="statusFilter" @change="fetchLeaveTypes">
          <option value="">All Status</option>
          <option value="1">Active</option>
          <option value="0">Inactive</option>
        </select>
        <button class="hr-btn hr-btn-success" @click="openAddModal">
          <i class="bx bx-plus-circle"></i> Add Leave Type
        </button>
      </div>
    </template>

    <div class="leave-types-content">
      <!-- Stats -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-list-ul"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Leave Types</p>
          <h2 class="hr-metric-value">{{ metrics.total || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Active</p>
          <h2 class="hr-metric-value">{{ metrics.active || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-x-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Inactive</p>
          <h2 class="hr-metric-value">{{ metrics.inactive || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-money"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Paid Leave Types</p>
          <h2 class="hr-metric-value">{{ metrics.paid || 0 }}</h2>
            </div>
        </div>
      </div>

      <!-- Main card -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-list-check"></i> Leave Types</h5>
          <div class="hr-search-wrap" style="min-width: 260px;">
            <input
              v-model="searchValue"
              type="text"
              class="hr-input"
              placeholder="Search leave types..."
              @keyup.enter="fetchLeaveTypes"
            >
            <i class="bx bx-search-alt search-icon"></i>
          </div>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-state">
            <div class="hr-spinner"></div>
            <p>Loading leave types...</p>
          </div>
          <div v-else-if="leaveTypes.length === 0" class="hr-empty-state">
            <i class="bx bx-file"></i>
            <h5>No leave types found</h5>
            <p>Add your first leave type to get started</p>
            <button class="hr-btn hr-btn-primary" @click="openAddModal">
              <i class="bx bx-plus hr-me-2"></i> Add Your First Leave Type
            </button>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Leave Type</th>
                  <th style="text-align: center;">Allocation Days</th>
                  <th style="text-align: center;">Paid</th>
                  <th style="text-align: center;">Requires Approval</th>
                  <th style="text-align: center;">Carry Forward</th>
                  <th style="text-align: center;">Max Carry Days</th>
                  <th style="text-align: center;">Eligible Gender</th>
                  <th style="text-align: center;">Calculation</th>
                  <th style="text-align: center;">Status</th>
                  <th style="text-align: center;">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in leaveTypes" :key="item.id">
                  <td>{{ index + 1 }}</td>
                  <td>
                    <div class="hr-flex hr-flex-center hr-gap-2">
                      <div class="type-avatar" :style="{ backgroundColor: item.color || '#5b73e8' }">
                        {{ getInitials(item.typeName) }}
                      </div>
                      <div>
                        <div class="type-name">{{ item.typeName }}</div>
                        <small v-if="item.description" class="type-desc">{{ item.description }}</small>
                      </div>
                    </div>
                  </td>
                  <td style="text-align: center;">
                    <span class="hr-badge hr-badge-primary">{{ item.allocationDays }} days</span>
                  </td>
                  <td style="text-align: center;">
                    <span class="hr-badge" :class="item.isPaid === '1' ? 'hr-badge-success' : 'hr-badge-muted'">
                      {{ item.isPaid === '1' ? 'Yes' : 'No' }}
                    </span>
                  </td>
                  <td style="text-align: center;">
                    <span class="hr-badge" :class="item.requiresApproval === '1' ? 'hr-badge-warning' : 'hr-badge-muted'">
                      {{ item.requiresApproval === '1' ? 'Yes' : 'No' }}
                    </span>
                  </td>
                  <td style="text-align: center;">
                    <span class="hr-badge" :class="item.allowCarryForward === '1' ? 'hr-badge-info' : 'hr-badge-muted'">
                      {{ item.allowCarryForward === '1' ? 'Yes' : 'No' }}
                    </span>
                  </td>
                  <td style="text-align: center;">{{ item.maxCarryForwardDays || '-' }}</td>
                  <td style="text-align: center;">
                    <span class="hr-badge hr-badge-muted">{{ formatEligibleGender(item.eligibleGender, item.typeName) }}</span>
                  </td>
                  <td style="text-align: center;">
                    <small>{{ formatCalculationMethod(item.calculationMethod) }}</small>
                  </td>
                  <td style="text-align: center;">
                    <label class="hr-toggle-wrap">
                      <input
                        type="checkbox"
                        :checked="item.isActive === '1'"
                        @change="toggleStatus(item)"
                        :disabled="item.toggling"
                      >
                      <span class="hr-toggle-slider"></span>
                    </label>
                  </td>
                  <td style="text-align: center;">
                    <div class="hr-flex hr-flex-center hr-gap-2" style="justify-content: center;">
                      <button class="hr-btn hr-btn-info hr-btn-sm hr-btn-icon" @click="openEditModal(item)" title="Edit">
                        <i class="bx bx-edit"></i>
                      </button>
                      <button class="hr-btn hr-btn-danger hr-btn-sm hr-btn-icon" @click="confirmDelete(item)" title="Delete">
                        <i class="bx bx-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Add/Edit Modal -->
      <ModalWrapper
        ref="formModal"
        :title="isEditing ? 'Edit Leave Type' : 'Add New Leave Type'"
        size="medium"
      >
        <template #body>
          <form @submit.prevent="saveLeaveType" class="leave-type-form">
            <div class="hr-mb-3">
              <label class="hr-form-label">Leave Type Name <span class="required">*</span></label>
              <input type="text" class="hr-input" v-model="formData.typeName" placeholder="e.g., Annual Leave, Sick Leave" required>
            </div>
            <div class="hr-mb-3">
              <label class="hr-form-label">Description</label>
              <textarea class="hr-textarea" v-model="formData.description" rows="2" placeholder="Brief description"></textarea>
            </div>
            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-form-label">Allocation Days <span class="required">*</span></label>
                <input type="number" class="hr-input" v-model="formData.allocationDays" min="0" step="0.5" required>
                <small class="hr-form-hint">Days allocated per year</small>
              </div>
              <div>
                <label class="hr-form-label">Color</label>
                <input type="color" class="hr-input-color" v-model="formData.color">
              </div>
            </div>
            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-checkbox-wrap">
                  <input type="checkbox" id="isPaid" v-model="formData.isPaid">
                  <span>Paid Leave</span>
                </label>
                <small class="hr-form-hint">Employee gets paid during leave</small>
              </div>
              <div>
                <label class="hr-checkbox-wrap">
                  <input type="checkbox" id="requiresApproval" v-model="formData.requiresApproval">
                  <span>Requires Approval</span>
                </label>
                <small class="hr-form-hint">Manager approval needed</small>
              </div>
            </div>
            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-checkbox-wrap">
                  <input type="checkbox" id="allowCarryForward" v-model="formData.allowCarryForward">
                  <span>Allow Carry Forward</span>
                </label>
                <small class="hr-form-hint">Unused days carry to next year</small>
              </div>
              <div v-if="formData.allowCarryForward">
                <label class="hr-form-label">Max Carry Forward Days</label>
                <input type="number" class="hr-input" v-model="formData.maxCarryForwardDays" min="0" placeholder="e.g., 5">
              </div>
            </div>
            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-form-label">Eligible Gender</label>
                <select class="hr-select" v-model="formData.eligibleGender">
                  <option value="all">All employees</option>
                  <option value="female">Female only (e.g. Maternity)</option>
                  <option value="male">Male only (e.g. Paternity)</option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Calculation Method</label>
                <select class="hr-select" v-model="formData.calculationMethod">
                  <option value="annual_fixed">Fixed annual days</option>
                  <option value="pro_rata_hire">Pro-rata from hire date</option>
                </select>
              </div>
            </div>
            <div class="hr-mb-3">
              <label class="hr-checkbox-wrap">
                <input type="checkbox" id="autoAssign" v-model="formData.autoAssign">
                <span>Auto-assign on employee onboarding</span>
              </label>
            </div>
            <div>
              <label class="hr-checkbox-wrap">
                <input type="checkbox" id="isActive" v-model="formData.isActive">
                <span>Active</span>
              </label>
              <small class="hr-form-hint">Inactive leave types cannot be used</small>
            </div>
          </form>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.formModal.close()">Cancel</button>
          <button type="button" class="hr-btn" :class="isEditing ? 'hr-btn-info' : 'hr-btn-success'" :disabled="saving" @click="saveLeaveType">
            <span v-if="saving" class="hr-spinner-inline"></span>
            <i v-else class="bx bx-check hr-me-2"></i>
            {{ isEditing ? 'Update' : 'Save' }}
          </button>
        </template>
      </ModalWrapper>

      <!-- Delete Modal -->
      <ModalWrapper ref="deleteModal" title="Confirm Delete" size="small">
        <template #body>
          <div class="text-center">
            <i class="bx bx-error-circle delete-icon"></i>
            <h5>Are you sure?</h5>
            <p class="hr-text-muted">You are about to delete <strong>{{ deleteItem?.typeName }}</strong>. This action cannot be undone.</p>
            <div v-if="forceDeleteRequired" class="hr-danger-box hr-mb-3">
              <strong>Warning:</strong> This leave type has:
              <ul>
                <li v-if="deleteStats.requestCount > 0">{{ deleteStats.requestCount }} leave request(s)</li>
                <li v-if="deleteStats.balanceCount > 0">{{ deleteStats.balanceCount }} leave balance(s)</li>
              </ul>
              <p>Type <strong>FORCE DELETE</strong> below to permanently delete:</p>
              <input
                type="text"
                class="hr-input hr-mt-2"
                v-model="forceDeleteText"
                placeholder="Type FORCE DELETE to confirm"
                :class="{ 'input-valid': forceDeleteText === 'FORCE DELETE', 'input-invalid': forceDeleteText && forceDeleteText !== 'FORCE DELETE' }"
              >
            </div>
            <div v-else class="hr-warning-box">
              <i class="bx bx-info-circle hr-me-2"></i>
              Employees with existing balances may be affected.
            </div>
          </div>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="closeDeleteModal">Cancel</button>
          <button
            type="button"
            class="hr-btn hr-btn-danger"
            @click="deleteLeaveType"
            :disabled="deleting || (forceDeleteRequired && forceDeleteText !== 'FORCE DELETE')"
          >
            <span v-if="deleting" class="hr-spinner-inline"></span>
            <i v-else class="bx bx-trash hr-me-2"></i>
            {{ forceDeleteRequired ? 'Force Delete' : 'Yes, Delete' }}
          </button>
        </template>
      </ModalWrapper>

      <!-- Success / Error Modals -->
      <ModalWrapper ref="successModal" title="Leave Type" size="small">
        <template #body>
          <div class="text-center">
            <i class="bx bx-check-circle success-icon"></i>
            <h4 class="success-heading">Success!</h4>
            <p>{{ successMessage }}</p>
          </div>
        </template>
        <template #footer>
          <button class="hr-btn hr-btn-success" @click="$refs.successModal.close(); fetchLeaveTypes()">OK</button>
        </template>
      </ModalWrapper>
      <ModalWrapper ref="errorModal" title="Error" size="small">
        <template #body>
          <div class="text-center">
            <i class="bx bx-error-circle error-icon"></i>
            <h4 class="error-heading">Operation Failed</h4>
            <p>{{ errorMessage }}</p>
          </div>
        </template>
        <template #footer>
          <button class="hr-btn hr-btn-danger" @click="$refs.errorModal.close()">OK</button>
        </template>
      </ModalWrapper>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute } from "@/api";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "LeaveTypesManagement",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      searchValue: '',
      statusFilter: '',
      leaveTypes: [],
      metrics: { total: 0, active: 0, inactive: 0, paid: 0 },
      isEditing: false,
      formData: {
        id: null,
        typeName: '',
        description: '',
        allocationDays: 21,
        color: '#5b73e8',
        isPaid: true,
        requiresApproval: true,
        allowCarryForward: true,
        maxCarryForwardDays: 5,
        eligibleGender: 'all',
        calculationMethod: 'annual_fixed',
        autoAssign: true,
        isActive: true
      },
      deleteItem: null,
      forceDeleteRequired: false,
      forceDeleteText: '',
      deleteStats: { requestCount: 0, balanceCount: 0 },
      successMessage: '',
      errorMessage: ''
    };
  },
  mounted() {
    this.fetchLeaveTypes();
  },
  methods: {
    getInitials(name) {
      if (!name) return 'LT';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },
    openAddModal() {
      this.isEditing = false;
      this.formData = {
        id: null,
        typeName: '',
        description: '',
        allocationDays: 21,
        color: '#5b73e8',
        isPaid: true,
        requiresApproval: true,
        allowCarryForward: true,
        maxCarryForwardDays: 5,
        eligibleGender: 'all',
        calculationMethod: 'annual_fixed',
        autoAssign: true,
        isActive: true
      };
      this.$nextTick(() => this.$refs.formModal?.open());
    },
    openEditModal(item) {
      this.isEditing = true;
      this.formData = {
        id: item.id,
        typeName: item.typeName,
        description: item.description || '',
        allocationDays: item.allocationDays,
        color: item.color || '#5b73e8',
        isPaid: item.isPaid === '1',
        requiresApproval: item.requiresApproval === '1',
        allowCarryForward: item.allowCarryForward === '1',
        maxCarryForwardDays: item.maxCarryForwardDays || 5,
        eligibleGender: item.eligibleGender || this.inferGenderFromName(item.typeName),
        calculationMethod: item.calculationMethod || 'annual_fixed',
        autoAssign: item.autoAssign === undefined ? true : (item.autoAssign === '1' || item.autoAssign === 1 || item.autoAssign === true),
        isActive: item.isActive === '1'
      };
      this.$nextTick(() => this.$refs.formModal?.open());
    },
    inferGenderFromName(typeName) {
      const name = (typeName || '').toLowerCase();
      if (name.includes('maternity')) return 'female';
      if (name.includes('paternity')) return 'male';
      return 'all';
    },
    formatEligibleGender(gender, typeName) {
      const value = gender || this.inferGenderFromName(typeName);
      if (value === 'female') return 'Female';
      if (value === 'male') return 'Male';
      return 'All';
    },
    formatCalculationMethod(method) {
      return method === 'pro_rata_hire' ? 'Pro-rata' : 'Fixed annual';
    },
    async saveLeaveType() {
      if (!this.formData.typeName) {
        this.showError('Leave Type Name is required');
        return;
      }
      if (!this.formData.allocationDays && this.formData.allocationDays !== 0) {
        this.showError('Allocation Days is required');
        return;
      }
      this.saving = true;
      const data = new FormData();
      data.append("function", this.isEditing ? "updateLeaveType" : "addLeaveType");
      if (this.isEditing) data.append("id", this.formData.id);
      data.append("typeName", this.formData.typeName);
      data.append("description", this.formData.description);
      data.append("allocationDays", this.formData.allocationDays);
      data.append("color", this.formData.color);
      data.append("isPaid", this.formData.isPaid ? '1' : '0');
      data.append("requiresApproval", this.formData.requiresApproval ? '1' : '0');
      data.append("allowCarryForward", this.formData.allowCarryForward ? '1' : '0');
      data.append("maxCarryForwardDays", this.formData.allowCarryForward ? this.formData.maxCarryForwardDays : 0);
      data.append("eligibleGender", this.formData.eligibleGender || 'all');
      data.append("calculationMethod", this.formData.calculationMethod || 'annual_fixed');
      data.append("autoAssign", this.formData.autoAssign ? '1' : '0');
      data.append("isActive", this.formData.isActive ? '1' : '0');
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.$refs.formModal?.close();
          this.showSuccess(res.data.message);
          await this.fetchLeaveTypes();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        this.showError('Failed to save leave type');
      } finally {
        this.saving = false;
      }
    },
    async toggleStatus(item) {
      item.toggling = true;
      const newStatus = item.isActive === '1' ? '0' : '1';
      const data = new FormData();
      data.append("function", "toggleLeaveTypeStatus");
      data.append("id", item.id);
      data.append("isActive", newStatus);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          item.isActive = newStatus;
          this.showSuccess(res.data.message);
          await this.fetchLeaveTypes();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        this.showError('Failed to update status');
      } finally {
        item.toggling = false;
      }
    },
    confirmDelete(item) {
      this.deleteItem = item;
      this.forceDeleteRequired = false;
      this.forceDeleteText = '';
      this.deleteStats = { requestCount: 0, balanceCount: 0 };
      this.$nextTick(() => this.$refs.deleteModal?.open());
    },
    resetDeleteModal() {
      this.forceDeleteRequired = false;
      this.forceDeleteText = '';
      this.deleteStats = { requestCount: 0, balanceCount: 0 };
    },
    closeDeleteModal() {
      this.resetDeleteModal();
      this.$refs.deleteModal?.close();
    },
    async deleteLeaveType() {
      if (!this.deleteItem) return;
      if (this.forceDeleteRequired && this.forceDeleteText !== 'FORCE DELETE') return;
      this.deleting = true;
      const data = new FormData();
      data.append("function", "deleteLeaveType");
      data.append("id", this.deleteItem.id);
      if (this.forceDeleteRequired) data.append("confirmText", this.forceDeleteText);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.$refs.deleteModal?.close();
          this.showSuccess(res.data.message);
          this.deleteItem = null;
          this.resetDeleteModal();
          await this.fetchLeaveTypes();
        } else {
          if (res.data.data?.requiresForceDelete) {
            this.forceDeleteRequired = true;
            this.deleteStats = {
              requestCount: res.data.data.requestCount || 0,
              balanceCount: res.data.data.balanceCount || 0
            };
          } else {
            this.showError(res.data.message);
          }
        }
      } catch (error) {
        this.showError('Failed to delete leave type');
      } finally {
        this.deleting = false;
      }
    },
    showSuccess(message) {
      this.successMessage = message;
      this.$nextTick(() => this.$refs.successModal?.open());
    },
    showError(message) {
      this.errorMessage = message;
      this.$nextTick(() => this.$refs.errorModal?.open());
    },
    async fetchLeaveTypes() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getLeaveTypesManagement");
      data.append("search", this.searchValue);
      data.append("status", this.statusFilter);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.leaveTypes = res.data.data.leaveTypes.map(item => ({ ...item, toggling: false }));
          this.metrics = res.data.data.metrics;
        }
      } catch (error) {
        this.showError('Failed to load leave types');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.type-avatar {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: white;
}

.type-name { font-weight: 600; font-size: 14px; }
.type-desc { font-size: 12px; color: #64748b; display: block; }

.hr-toggle-wrap {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.hr-toggle-wrap input {
  opacity: 0;
  width: 0;
  height: 0;
}

.hr-toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  border-radius: 24px;
  transition: 0.3s;
}

.hr-toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.3s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.hr-toggle-wrap input:checked + .hr-toggle-slider {
  background-color: #4f46e5;
}

.hr-toggle-wrap input:checked + .hr-toggle-slider:before {
  transform: translateX(20px);
}

.hr-toggle-wrap input:disabled + .hr-toggle-slider {
  opacity: 0.6;
  cursor: not-allowed;
}

.hr-input-color {
  width: 100%;
  height: 42px;
  padding: 4px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
}

.delete-icon { font-size: 48px; color: #ef4444; margin-bottom: 12px; }
.success-icon { font-size: 48px; color: #10b981; margin-bottom: 12px; }
.error-icon { font-size: 48px; color: #ef4444; margin-bottom: 12px; }
.success-heading { color: #10b981; margin: 0 0 8px 0; }
.error-heading { color: #ef4444; margin: 0 0 8px 0; }

.hr-danger-box {
  padding: 16px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #b91c1c;
  text-align: left;
}

.hr-warning-box {
  padding: 12px 16px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  color: #b45309;
  font-size: 14px;
}

.input-valid { border-color: #10b981; }
.input-invalid { border-color: #ef4444; }

.hr-spinner-inline {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
